import { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme } from '@mui/material';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { interpolate } from '../utils/entryType';
import { HORIZON_MONTHLY, HORIZON_PLANNING, horizonOf } from '../utils/budgetsByCategory';

/**
 * Horizon switch confirmation (#2175): opened by a HorizonChip press. Explains
 * the two horizons in the same two sentences the chip's hover note shows,
 * says which one the budget has now, and on Switch sends the other horizon
 * through `PUT /budgets/{id}` with the version lock. The caller updates its
 * own state from `onSwitched(updatedBudget)`; the dialog never reloads.
 *
 * @param {object} props
 * @param {object|null} props.budget     the budget to switch; null keeps the dialog closed
 * @param {function} props.onClose       Cancel, Escape or the backdrop
 * @param {function} props.onSwitched    called with the updated budget after a successful save
 */
const HorizonSwitchDialog = ({ budget, onClose, onSwitched }) => {
  const { activeGroupId } = useGroup();
  const strings = useEngineStrings();
  const { ds, palette } = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { setSubmitting(false); setError(''); }, [budget]);

  const open = budget != null;
  const current = open ? horizonOf(budget) : HORIZON_MONTHLY;
  const next = current === HORIZON_PLANNING ? HORIZON_MONTHLY : HORIZON_PLANNING;
  const name = open ? budget.name : '';
  const fullName = (h) => strings[h === HORIZON_PLANNING ? 'budgets.horizonPlanningAhead' : 'budgets.horizonMonthly'];
  const title = interpolate(strings[next === HORIZON_PLANNING ? 'budgets.horizonSwitchToPlanningTitle' : 'budgets.horizonSwitchToMonthlyTitle'], { name });

  // The horizon name inside each sentence reads in weight 500.
  const emphasized = (sentence, horizonName) => sentence.split(horizonName).flatMap((part, i, parts) => (
    i < parts.length - 1
      ? [part, <Box key={i} component="span" sx={{ fontWeight: ds.font.weightMedium }}>{horizonName}</Box>]
      : [part]
  ));

  const handleSwitch = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await api.put(activeGroupId, `/budgets/${encodeURIComponent(budget.budgetId)}`, { horizon: next, version: budget.version });
      onSwitched(response?.budget || { ...budget, horizon: next });
    } catch (err) {
      setSubmitting(false);
      setError(err.message || strings['budgets.horizonSwitchFailed']);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth data-testid="horizon-switch-dialog">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: `${ds.space[3]}px` }}>{emphasized(strings['budgets.horizonExplainMonthly'], fullName(HORIZON_MONTHLY))}</Typography>
        <Typography sx={{ mb: `${ds.space[3]}px` }}>{emphasized(strings['budgets.horizonExplainPlanning'], fullName(HORIZON_PLANNING))}</Typography>
        <Typography>{emphasized(interpolate(strings['budgets.horizonNow'], { name, horizon: fullName(current) }), fullName(current))}</Typography>
        {error && (
          <Typography role="alert" sx={{ mt: `${ds.space[3]}px`, color: palette.error.main, fontSize: '13px' }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{strings['budgets.horizonCancel']}</Button>
        <Button onClick={handleSwitch} variant="contained" disabled={submitting}>{strings['budgets.horizonSwitch']}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HorizonSwitchDialog;
